import { POSITION_PROPERTIES } from './constants';
import type { CycleData, CycleBounds, TimePosition } from '../types';

export function timeToX(
	time: number,
	videoDuration: number,
	canvasWidth: number,
	margin: number = POSITION_PROPERTIES.MIN_POSITION
): number {
	if (videoDuration <= POSITION_PROPERTIES.MIN_POSITION) return margin;

	const effectiveWidth = canvasWidth - margin * POSITION_PROPERTIES.MARGIN_MULTIPLIER;
	const ratio = time / videoDuration;
	const positionX = margin + ratio * effectiveWidth;

	return positionX;
}

export function xToTime(
	x: number,
	videoDuration: number,
	canvasWidth: number,
	margin: number = POSITION_PROPERTIES.MIN_POSITION
): number {
	if (canvasWidth <= margin * POSITION_PROPERTIES.MARGIN_MULTIPLIER) {
		return POSITION_PROPERTIES.MIN_POSITION;
	}

	const effectiveWidth = canvasWidth - margin * POSITION_PROPERTIES.MARGIN_MULTIPLIER;
	const adjustedX = Math.max(POSITION_PROPERTIES.MIN_POSITION, x - margin);
	const ratio = adjustedX / effectiveWidth;
	const clampedTime = Math.max(POSITION_PROPERTIES.MIN_POSITION, Math.min(videoDuration, ratio * videoDuration));

	return clampedTime;
}

export function getCycleBounds(
	cycle: CycleData,
	videoDuration: number,
	canvasWidth: number,
	margin: number = POSITION_PROPERTIES.MIN_POSITION
): CycleBounds {
	const startX = timeToX(cycle.start, videoDuration, canvasWidth, margin);
	const endX = timeToX(cycle.end, videoDuration, canvasWidth, margin);

	return {
		startX,
		endX
	};
}

export function isXWithinCycle(
	x: number,
	cycle: CycleData,
	videoDuration: number,
	canvasWidth: number,
	margin = 0
): boolean {
	const bounds = getCycleBounds(cycle, videoDuration, canvasWidth, margin);
	const isAfterStart = x >= bounds.startX;
	const isBeforeEnd = x <= bounds.endX;

	return isAfterStart && isBeforeEnd;
}

export function isWithinCycleBar(
	x: number,
	y: number,
	cycle: CycleData,
	videoDuration: number,
	canvasWidth: number,
	canvasHeight: number,
	cycleBarHeight: number,
	margin = 0
): boolean {
	const isWithinCycleX = isXWithinCycle(x, cycle, videoDuration, canvasWidth, margin);

	const barTop = canvasHeight - cycleBarHeight;
	const barBottom = canvasHeight;
	const isWithinBarY = y >= barTop && y <= barBottom;

	return isWithinCycleX && isWithinBarY;
}

export function isXNearCycleEdge(
	x: number,
	cycle: CycleData,
	videoDuration: number,
	canvasWidth: number,
	threshold: number,
	edge: TimePosition,
	margin = 0
): boolean {
	const bounds = getCycleBounds(cycle, videoDuration, canvasWidth, margin);
	const targetX = edge === 'start' ? bounds.startX : bounds.endX;
	const distanceToEdge = Math.abs(x - targetX);
	const isWithinThreshold = distanceToEdge <= threshold;

	return isWithinThreshold;
}
